// Lightweight Cognito Hosted UI integration. We deliberately don't pull
// the full amazon-cognito-identity-js SDK: for OAuth code-flow with PKCE
// against the Hosted UI, the protocol is small enough to implement here
// and skipping the SDK keeps bundle size down meaningfully.

import { config } from './config.js';
import { loginPath, safeReturnTo } from './login-path.js';
import { hasUnsavedChanges, onUnsavedChangesCleared } from './unsaved-changes.js';

// SEC-009 (ENTRAID-250): tokens live in memory only. Nothing that can mint or
// replay a session is ever written to Web Storage (the one localStorage entry,
// the remembered native account of ENTRAID-284 and the last app client id, are
// an email and a marker, and a public identifier, only), and the refresh token is
// discarded at the token exchange (the SPA never refreshes; it re-runs the
// authorize flow). A reload therefore drops the tokens and re-authenticates
// silently against the hosted UI session (prompt=none), falling back to the
// email-first /login page when there is no session.
let memoryTokens = null;

// One sign-in transaction (PKCE verifier + OAuth state + return path) must
// survive the full-page round trip to the IdP, so it is kept in
// sessionStorage and deleted as soon as the callback consumes it. It holds no
// token: the verifier is useless without the one-time code bound to it.
const STORAGE_TX = 'onguard.authTx';
// Legacy keys from before SEC-009 (tokens incl. refresh token, PKCE, return
// path). Purged on load so an old tab never leaves a refresh token behind.
const LEGACY_KEYS = ['onguard.tokens', 'onguard.pkce', 'onguard.returnTo'];
try {
  for (const k of LEGACY_KEYS) sessionStorage.removeItem(k);
} catch {
  /* storage unavailable: nothing to purge */
}

export function isAuthenticated() {
  const t = getTokens();
  if (!t) return false;
  if (!t.idToken || !t.idTokenExp) return false;
  return t.idTokenExp * 1000 > Date.now() + 30_000;
}

export function getTokens() {
  return memoryTokens;
}

export function getIdTokenClaims() {
  const t = getTokens();
  if (!t?.idToken) return null;
  return decodeJwt(t.idToken);
}

// Type-your-email-first: ask the platform which sign-in method serves this
// email's domain BEFORE authenticating. The app shows one email field and
// never a menu of providers; the returned method decides where we redirect.
//   sso     -> the domain is bound to a tenant IdP (its identifier is returned)
//   native  -> Cognito-native (password + MFA)
//   unknown -> discovery failed; the native password form (fail-safe, see
//              resolveSignIn: never a chooser that could list other IdPs)
export async function discoverLoginMethod(email) {
  try {
    const url = `${config.provisioningApiUrl}/discover?email=${encodeURIComponent(email)}`;
    const res = await fetch(url);
    if (!res.ok) return { method: 'unknown' };
    const data = await res.json();
    if (data?.method === 'sso') return { method: 'sso', idpIdentifier: data.idpIdentifier };
    if (data?.method === 'native') return { method: 'native' };
    return { method: 'unknown' };
  } catch {
    return { method: 'unknown' };
  }
}

// --- login hint (the email a flow already collected, e.g. the onboarding claim) ---
// Kept in sessionStorage only (never localStorage) so it dies with the tab, and
// cleared as soon as it has been used to start a sign-in. It is a convenience
// for routing and prefill, never an authentication input.
const STORAGE_LOGIN_HINT = 'onguard.loginHint';

export function rememberLoginHint(email) {
  const value = typeof email === 'string' ? email.trim() : '';
  if (!value) return;
  try {
    sessionStorage.setItem(STORAGE_LOGIN_HINT, value);
  } catch {
    /* best-effort: without storage the user simply types the email again */
  }
}

export function peekLoginHint() {
  try {
    return sessionStorage.getItem(STORAGE_LOGIN_HINT) || '';
  } catch {
    return '';
  }
}

export function clearLoginHint() {
  try {
    sessionStorage.removeItem(STORAGE_LOGIN_HINT);
  } catch {
    /* best-effort */
  }
}

// --- returning native user (ENTRAID-284) ---
// After a successful NATIVE (password) sign-in the browser remembers two
// things in localStorage, in one entry: the email (from the verified ID token)
// and a "native" marker. The next visit then skips the portal's email step and
// goes straight to the native client's password page with the email as
// login_hint. Security posture:
//  - It holds no token, code, verifier or state, so it cannot mint or replay a
//    session (SEC-009 stands). The marker grants nothing: the user still has to
//    authenticate at the IdP, MFA included. The email is only a login_hint.
//  - Every read is validated (exact shape, plausible email); anything else is
//    treated as "nothing remembered".
//  - Sign-out clears it; a silent re-authentication leaves it alone. Every
//    storage access is best-effort (private mode).
//  - A successful SSO sign-in is remembered the same way with an 'sso' marker
//    (Hugo Andrade, 2026-10-29: staff typed their full email every time). The
//    next visit runs the same domain discovery with that email and goes to the
//    IdP it names; the stored method never picks the IdP. "Use a different
//    account" on the sign-in page forgets it.
// The key keeps its first name so an account remembered before still works.
const STORAGE_NATIVE_SIGN_IN = 'onguard.nativeSignIn';
const REMEMBERED_METHODS = new Set(['native', 'sso']);
const MAX_EMAIL_LENGTH = 254;

function plausibleEmail(value) {
  return typeof value === 'string' && value.length <= MAX_EMAIL_LENGTH && /^[^\s@]+@[^\s@]+$/.test(value);
}

// Returns true when the account was stored.
export function rememberNativeSignIn(email) {
  return rememberSignIn(email, 'native');
}

// Returns true when the account was stored. `method` is 'native' or 'sso'.
export function rememberSignIn(email, method) {
  const value = typeof email === 'string' ? email.trim() : '';
  if (!plausibleEmail(value) || !REMEMBERED_METHODS.has(method)) return false;
  try {
    localStorage.setItem(STORAGE_NATIVE_SIGN_IN, JSON.stringify({ method, email: value }));
    return true;
  } catch {
    /* best-effort: without storage the user simply gets the email step */
    return false;
  }
}

// The last account that signed in here, `{ method, email }`, or null.
export function readRememberedSignIn() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_NATIVE_SIGN_IN) || 'null');
    if (!stored || typeof stored !== 'object') return null;
    if (!REMEMBERED_METHODS.has(stored.method) || !plausibleEmail(stored.email)) return null;
    return { method: stored.method, email: stored.email };
  } catch {
    return null;
  }
}

export function readNativeSignIn() {
  const remembered = readRememberedSignIn();
  return remembered?.method === 'native' ? { email: remembered.email } : null;
}

export function clearNativeSignIn() {
  try {
    localStorage.removeItem(STORAGE_NATIVE_SIGN_IN);
  } catch {
    /* best-effort */
  }
}

// At most one automatic redirect to the native sign-in per tab inside
// NATIVE_AUTO_WINDOW_MS, so coming back from the IdP (Back button, a failed
// round trip) can never bounce between the portal and the IdP: the second
// arrival shows the choice instead. Like the silent-attempt cap, without
// usable sessionStorage it never redirects automatically.
const STORAGE_NATIVE_AUTO = 'onguard.nativeAutoAt';
const NATIVE_AUTO_WINDOW_MS = 60_000;

export function nativeAutoLoginRecentlyTried(now = Date.now()) {
  try {
    const at = Number(sessionStorage.getItem(STORAGE_NATIVE_AUTO));
    return Number.isFinite(at) && at > 0 && now - at < NATIVE_AUTO_WINDOW_MS;
  } catch {
    return true;
  }
}

// Returns true when the attempt was recorded, i.e. the caller may redirect.
export function markNativeAutoLogin(now = Date.now()) {
  try {
    sessionStorage.setItem(STORAGE_NATIVE_AUTO, String(now));
    return true;
  } catch {
    return false;
  }
}

function clearNativeAutoAttempt() {
  try {
    sessionStorage.removeItem(STORAGE_NATIVE_AUTO);
  } catch {
    /* best-effort */
  }
}

// --- which central app client serves a sign-in (IPCMAN-1458, ENTRAID-256) ---
// The central pool gives this app two clients. `onguard-entraid` (main) holds
// the federated IdPs and is only ever used with idp_identifier, so it goes
// straight to the IdP. `onguard-entraid-native` allows only COGNITO, so the
// password page never shows a "Continue with <IdP>" button (Managed Login v2
// lists every IdP a client allows, even with identity_provider=COGNITO).
// A failed discovery ('unknown') also takes the native client: the password
// form is where customers sign in, and it never lists anyone's IdP. A silent
// re-authentication (prompt=none) reuses the client of this tab's previous
// session, since the hosted UI session belongs to the IdP that client allows.
// Without a configured native client (older runtime config) the main client
// keeps the previous behavior.
const STORAGE_LAST_CLIENT = 'onguard.lastClient';

// The last client id is also kept in localStorage so a tab opened from a link
// (middle click) silently re-authenticates through the right client instead of
// the default one, which answers login_required for a session that belongs to
// the other client. It is a public identifier, not a credential: it cannot
// mint or replay a session, and a read is only honored when it is one of the
// two configured clients (a tampered value falls back to the default). Tokens
// are never shared across tabs. Sign-out clears it.
function readStoredClient(storage) {
  try {
    const id = storage.getItem(STORAGE_LAST_CLIENT);
    return typeof id === 'string' && [config.cognitoUserPoolClientId, config.cognitoNativeClientId].filter(Boolean).includes(id) ? id : '';
  } catch {
    return '';
  }
}

function readLastClient() {
  return readStoredClient(sessionStorage) || readStoredClient(localStorage);
}

// A client id read back from storage is only honored when it is one of this
// app's two configured clients; anything else yields the fallback.
function knownClientId(id, fallback = config.cognitoUserPoolClientId) {
  const known = [config.cognitoUserPoolClientId, config.cognitoNativeClientId].filter(Boolean);
  return typeof id === 'string' && known.includes(id) ? id : fallback;
}

export function resolveSignIn({ method, idpIdentifier, silent = false } = {}) {
  const mainId = config.cognitoUserPoolClientId;
  const nativeId = config.cognitoNativeClientId;
  if (silent) return { clientId: knownClientId(readLastClient(), nativeId || mainId) };
  if (method === 'sso' && idpIdentifier) return { clientId: mainId, method: 'sso', idpIdentifier };
  if (nativeId) return { clientId: nativeId, method: 'native' };
  return { clientId: mainId, method, idpIdentifier };
}

// The client that issued the current session. The code exchange, sign-out
// and any re-authentication use it: Cognito binds a code (and a refresh
// token, which this SPA never keeps) to the client that started the sign-in.
export function tokenClientId() {
  return knownClientId(getTokens()?.clientId ?? readLastClient());
}

// Build the /oauth2/authorize query. The method-specific hint is what lets us
// skip the Cognito chooser entirely: idp_identifier jumps straight to the
// tenant IdP (main client), identity_provider=COGNITO the native form (on the
// native-only client, so no federated buttons), and 'unknown' omits both.
// login_hint prefills the email.
// state (SEC-018) binds the callback to the tab that started the sign-in, on
// top of PKCE. prompt=none asks for a silent sign-in against an existing
// hosted UI session (the IdP answers error=login_required when there is none).
export function buildAuthorizeParams({ clientId, redirectUri, challenge, state, method, idpIdentifier, email, prompt }) {
  if (!state) throw new Error('buildAuthorizeParams: state is required');
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  if (prompt) params.set('prompt', prompt);
  if (email) params.set('login_hint', email);
  if (method === 'sso' && idpIdentifier) params.set('idp_identifier', idpIdentifier);
  else if (method === 'native') params.set('identity_provider', 'COGNITO');
  return params;
}

function readTx() {
  try {
    const tx = JSON.parse(sessionStorage.getItem(STORAGE_TX) || 'null');
    return tx && typeof tx === 'object' ? tx : null;
  } catch {
    return null;
  }
}

function clearTx() {
  try {
    sessionStorage.removeItem(STORAGE_TX);
  } catch {
    /* best-effort */
  }
}

export async function startLogin(returnTo = '/', { method, idpIdentifier, email, silent = false } = {}) {
  const verifier = generateRandomString(64);
  const state = generateRandomString(32);
  const challenge = await sha256base64url(verifier);
  const signIn = resolveSignIn({ method, idpIdentifier, silent });
  sessionStorage.setItem(STORAGE_TX, JSON.stringify({
    verifier,
    state,
    returnTo: safeReturnTo(returnTo),
    silent: Boolean(silent),
    // The code must be redeemed by the client that requests it.
    clientId: signIn.clientId,
  }));
  // The hint has done its job once a sign-in starts; do not leave it behind.
  if (!silent) clearLoginHint();

  const params = buildAuthorizeParams({
    clientId: signIn.clientId,
    redirectUri: `${window.location.origin}/callback`,
    challenge,
    state,
    method: signIn.method,
    idpIdentifier: signIn.idpIdentifier,
    email,
    prompt: silent ? 'none' : undefined,
  });

  window.location.assign(`${config.cognitoHostedUiDomain}/oauth2/authorize?${params}`);
}

// --- silent re-authentication after a reload ---
// At most one prompt=none attempt per tab inside SILENT_WINDOW_MS, so a
// missing session can never bounce between the app and the IdP: the second
// time a page finds no tokens it falls back to its interactive sign-in path.
const STORAGE_SILENT = 'onguard.silentAuthAt';
const SILENT_WINDOW_MS = 60_000;

export function silentLoginRecentlyTried(now = Date.now()) {
  try {
    const at = Number(sessionStorage.getItem(STORAGE_SILENT));
    return Number.isFinite(at) && at > 0 && now - at < SILENT_WINDOW_MS;
  } catch {
    // Without storage we cannot cap the bounce, so never try silently.
    return true;
  }
}

function clearSilentAttempt() {
  try {
    sessionStorage.removeItem(STORAGE_SILENT);
  } catch {
    /* best-effort */
  }
}

/**
 * Try to restore the session without any UI. Returns true when a redirect to
 * the IdP is under way (the caller renders nothing more), false when a silent
 * attempt was already made recently and the caller should use its normal
 * interactive path (the email-first page, or the onboarding login hint).
 */
export function startSilentLogin(returnTo = '/', now = Date.now()) {
  if (silentLoginRecentlyTried(now)) return false;
  try {
    sessionStorage.setItem(STORAGE_SILENT, String(now));
  } catch {
    return false;
  }
  renewalUnderway = true;
  startLogin(returnTo, { silent: true }).catch(() => {
    window.location.assign(loginPath(returnTo));
  });
  return true;
}

/**
 * ENTRAID-350: the caller just gained a membership (an onboarding claim with an
 * existing login). The current ID token was issued before it, so its `tenants`
 * claim (written by the central IdP's pre-token trigger) does not list the new
 * customer and every route of that customer would refuse it
 * (backend lib/tenant-context.mjs). There is no refresh token (SEC-009), so the
 * one way to a fresh token is the same silent prompt=none authorize, back to
 * returnTo. Called once, right after the claim succeeded (the pending claim is
 * already cleared, so nothing repeats it); a hosted UI that answers
 * login_required comes back to returnTo, where the page takes its normal
 * sign-in path. The silent-attempt stamp is set here too, so that page goes
 * interactive at once instead of trying prompt=none a second time.
 */
export function refreshSessionForNewMembership(returnTo, now = Date.now()) {
  try {
    sessionStorage.setItem(STORAGE_SILENT, String(now));
  } catch {
    /* best-effort: the page's own silent-attempt cap still bounds it */
  }
  renewalUnderway = true;
  startLogin(returnTo, { silent: true }).catch(() => {
    window.location.assign(loginPath(returnTo));
  });
}

// --- keeping the session alive while a page stays open (ENTRAID-342) ---
// Tokens are memory-only and there is no refresh token (SEC-009, Appendix F),
// so the one way to renew is the same silent prompt=none authorize against the
// shared hosted UI session, through the client that issued the session. It is
// a full-page round trip that returns to the same path (the returnTo machinery),
// so it runs a few minutes BEFORE the id token expires, never on a hidden tab,
// and always through startSilentLogin plus the 60-second renewal stamp below:
// these two caps are the loop guard (a second attempt inside the window returns
// false, and nothing here retries). A renewal the hosted UI refuses (login_required) comes back
// through Callback to the same path, where AppShell finds no tokens, the cap
// blocks a second silent try and the user lands on /login?returnTo=<path>.
const RENEW_LEAD_MS = 5 * 60_000;
const MAX_TIMER_MS = 2 ** 31 - 1;
const STORAGE_RENEWED_NOTICE = 'onguard.sessionRenewedNotice';
// A successful sign-in clears the silent-attempt cap (completeLogin), so a
// renewal that "succeeds" but is followed by a request refused again (a 401
// for a fresh token, or tokens shorter than the lead time) would bounce
// forever. This stamp is NOT cleared by a sign-in: at most one renewal started
// by the watcher or by a 401 per tab inside RENEWAL_WINDOW_MS. Without usable
// storage it never renews automatically.
const STORAGE_RENEWAL_AT = 'onguard.sessionRenewalAt';
const RENEWAL_WINDOW_MS = 60_000;

function renewalRecentlyStarted(now) {
  try {
    const at = Number(sessionStorage.getItem(STORAGE_RENEWAL_AT));
    return Number.isFinite(at) && at > 0 && now - at < RENEWAL_WINDOW_MS;
  } catch {
    return true;
  }
}

// True when a silent renewal is under way (started here or joined).
function beginSessionRenewal(returnTo, now = Date.now()) {
  if (renewalUnderway) return true;
  if (renewalRecentlyStarted(now)) return false;
  try {
    sessionStorage.setItem(STORAGE_RENEWAL_AT, String(now));
  } catch {
    return false;
  }
  return startSilentLogin(returnTo, now);
}

// True once a silent renewal redirect has started in this page: concurrent 401s
// join it instead of racing a second one.
let renewalUnderway = false;
// A page restored from the back/forward cache keeps its JS state, including a
// renewalUnderway left true by a redirect the user navigated back from; that
// would make every later 401 "join" a renewal that is not happening.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) renewalUnderway = false;
  });
}

/**
 * Milliseconds until the session should be renewed (0 = now); null without a
 * session, and null when the token lifetime (exp - iat) is not longer than the
 * lead time: renewing 5 minutes early would then fire at once after every
 * sign-in. Such sessions are renewed only by the 401 path.
 */
export function msUntilRenewal(now = Date.now()) {
  const exp = memoryTokens?.idTokenExp;
  if (!exp) return null;
  const iat = decodeJwt(memoryTokens.idToken)?.iat;
  if (typeof iat === 'number' && (exp - iat) * 1000 <= RENEW_LEAD_MS) return null;
  return Math.max(0, exp * 1000 - RENEW_LEAD_MS - now);
}

/**
 * Renew the session before it expires: on a timer RENEW_LEAD_MS ahead of
 * idTokenExp, and when the tab becomes visible again (timers are throttled
 * while hidden and a hidden tab never navigates away on its own). getReturnTo
 * supplies the path to come back to. Returns a cleanup function.
 */
export function watchSessionRenewal({ getReturnTo }) {
  let timer = null;
  const stop = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const schedule = () => {
    stop();
    const ms = msUntilRenewal();
    if (ms === null) return;
    timer = setTimeout(fire, Math.min(ms, MAX_TIMER_MS));
  };
  function fire() {
    timer = null;
    const ms = msUntilRenewal();
    if (ms === null) return;
    if (ms > 0) {
      schedule(); // timer clamp or clock drift: not due yet
      return;
    }
    if (hidden()) return; // renewed when the tab is shown again
    // A full-page renewal reloads the route and drops unsaved input: wait until
    // the forms are saved or closed (onUnsavedChangesCleared) or the next
    // visibility change. The 401 path is unaffected.
    if (hasUnsavedChanges()) return;
    beginSessionRenewal(getReturnTo()); // false when capped: no retry, no loop
  }
  const onVisibility = () => {
    if (hidden()) return;
    if (msUntilRenewal() === 0) fire();
    else schedule();
  };
  document.addEventListener('visibilitychange', onVisibility);
  const offCleared = onUnsavedChangesCleared(onVisibility);
  schedule();
  return () => {
    stop();
    offCleared();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/**
 * The API answered 401 for a request that carried a token. Start (or join) one
 * silent renewal back to returnTo. Returns true when a renewal is under way,
 * false when the cap refused it (the caller then surfaces the error as before).
 * writeDropped marks a non-idempotent request that was NOT replayed: after the
 * round trip AppShell shows "Your session was renewed, please try again".
 */
export function renewAfterUnauthorized(returnTo, { writeDropped = false } = {}, now = Date.now()) {
  if (!beginSessionRenewal(returnTo, now)) return false;
  if (writeDropped) {
    try {
      sessionStorage.setItem(STORAGE_RENEWED_NOTICE, '1');
    } catch {
      /* best-effort: the thrown error still carries the message */
    }
  }
  return true;
}

/** One-shot: true once after a renewal that dropped a write. */
export function takeSessionRenewedNotice() {
  try {
    const set = sessionStorage.getItem(STORAGE_RENEWED_NOTICE) === '1';
    sessionStorage.removeItem(STORAGE_RENEWED_NOTICE);
    return set;
  } catch {
    return false;
  }
}

// Errors the IdP returns for prompt=none when interaction would be needed.
const SILENT_FALLBACK_ERRORS = new Set([
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required',
]);

/**
 * Callback with ?error=...: when it answers OUR silent attempt (state
 * matches) and only says the user must interact, return the path to resume
 * at, where the page takes its interactive sign-in path. Any other error, or
 * a state we did not issue, returns null so the callback shows the error.
 */
export function resumeAfterSilentLoginFailure({ error, state }) {
  const tx = readTx();
  clearTx();
  if (!tx || !tx.silent || !state || state !== tx.state) return null;
  if (!SILENT_FALLBACK_ERRORS.has(error)) return null;
  return safeReturnTo(tx.returnTo);
}

export async function completeLogin({ code, state } = {}) {
  const tx = readTx();
  // One-shot: whatever happens next, this transaction cannot be replayed.
  clearTx();
  if (!tx?.verifier || !tx?.state) throw new Error('No sign-in in progress in this tab. Please sign in again.');
  // SEC-018: reject a callback this tab did not start (login CSRF / code injection).
  if (!state || state !== tx.state) throw new Error('Sign-in state mismatch. Please sign in again.');
  if (!code) throw new Error('Missing authorization code in callback URL.');

  // Redeem the code with the client that requested it (main or native).
  const clientId = knownClientId(tx.clientId);
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code,
    redirect_uri: `${window.location.origin}/callback`,
    code_verifier: tx.verifier,
  });
  const res = await fetch(`${config.cognitoHostedUiDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`);
  const data = await res.json();

  const claims = decodeJwt(data.id_token);
  // data.refresh_token is deliberately dropped (SEC-009).
  memoryTokens = {
    idToken: data.id_token,
    accessToken: data.access_token,
    idTokenExp: claims?.exp,
    clientId,
  };
  // A client id is public (not a credential); kept so a reload re-authenticates
  // silently through the same client.
  try {
    sessionStorage.setItem(STORAGE_LAST_CLIENT, clientId);
  } catch {
    /* best-effort: a silent retry then uses the default client */
  }
  try {
    localStorage.setItem(STORAGE_LAST_CLIENT, clientId);
  } catch {
    /* best-effort: a new tab then uses the default client */
  }
  clearSilentAttempt();
  clearNativeAutoAttempt();
  // ENTRAID-284: an interactive sign-in updates what the browser remembers.
  // The native client redeemed the code -> remember that account as native;
  // the main client (only ever used with idp_identifier) -> remember it as
  // sso. No usable email -> forget a stale account. A silent
  // re-authentication changes nothing.
  if (!tx.silent) {
    const viaNative = Boolean(config.cognitoNativeClientId) && clientId === config.cognitoNativeClientId;
    if (!rememberSignIn(claims?.email, viaNative ? 'native' : 'sso')) clearNativeSignIn();
  }
  return safeReturnTo(tx.returnTo);
}

export function logout() {
  // Sign out through the client that issued the session (read before clearing).
  const clientId = tokenClientId();
  memoryTokens = null;
  clearTx();
  // Signing out forgets the remembered account: the next visit starts at the email step.
  clearNativeSignIn();
  clearNativeAutoAttempt();
  try {
    sessionStorage.removeItem(STORAGE_RENEWAL_AT);
    sessionStorage.removeItem(STORAGE_RENEWED_NOTICE);
    sessionStorage.removeItem(STORAGE_LAST_CLIENT);
  } catch {
    /* best-effort */
  }
  try {
    localStorage.removeItem(STORAGE_LAST_CLIENT);
  } catch {
    /* best-effort */
  }
  const params = new URLSearchParams({
    client_id: clientId,
    logout_uri: window.location.origin + '/',
  });
  window.location.assign(`${config.cognitoHostedUiDomain}/logout?${params}`);
}

// Test seam: reset the in-memory session between tests.
export function __resetAuthForTests() {
  memoryTokens = null;
  renewalUnderway = false;
}

// --- helpers ---

function decodeJwt(jwt) {
  try {
    const payload = jwt.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function generateRandomString(length) {
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => ('0' + (b & 0xff).toString(16)).slice(-2)).join('');
}

async function sha256base64url(input) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  const bytes = new Uint8Array(buf);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
